/**
 * Apoderamientos por comprobar — `TDS-CORE` Parte 4 §3.1, `OD-0031` D-05 y la
 * enmienda de `OD-0020` del 2026-09-21 (DRP-01 A).
 *
 * Es la ÚNICA escritura del backoffice sobre datos de un cliente, y no es dato
 * fiscal: no toca facturas, registros, huellas ni snapshots.
 *
 * Lo que la persona hace ANTES de pulsar, en la Sede y con el certificado de
 * Jominicol: «Consultar apoderamientos recibidos», y comprobar que el
 * poderdante es ese NIF, que el apoderado somos nosotros, que el poder cubre
 * VERI*FACTU y que está vigente. La pantalla lo recuerda en cada fila, porque es
 * lo único que evita activar por error.
 *
 * Vivía en el producto (`apps/web`, `/interno`). Se ha traído aquí por la
 * enmienda del 2026-09-28: el backoffice no es una pantalla del producto.
 */
import { useCallback, useEffect, useState } from "react";
import { api, type MotivoRechazo, type Pendiente } from "../api/cliente";
import { t } from "../textos";

const MOTIVOS: ReadonlyArray<{ readonly valor: MotivoRechazo; readonly texto: string }> = [
  { valor: "NOT_FOUND", texto: t.apoderamientos.motivos.NOT_FOUND },
  { valor: "GRANTOR_MISMATCH", texto: t.apoderamientos.motivos.GRANTOR_MISMATCH },
  { valor: "WRONG_PROCEDURE", texto: t.apoderamientos.motivos.WRONG_PROCEDURE },
  { valor: "WRONG_REPRESENTATIVE", texto: t.apoderamientos.motivos.WRONG_REPRESENTATIVE },
  { valor: "NOT_IN_FORCE", texto: t.apoderamientos.motivos.NOT_IN_FORCE },
  { valor: "OTHER", texto: t.apoderamientos.motivos.OTHER },
];

function fechaLegible(iso: string | null): string {
  if (iso === null) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function Apoderamientos() {
  const [estado, setEstado] = useState<"cargando" | "ok" | "no-encontrado" | "sin-mfa" | "error">("cargando");
  const [pendientes, setPendientes] = useState<readonly Pendiente[]>([]);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [rechazando, setRechazando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState<MotivoRechazo>("NOT_FOUND");
  const [mensaje, setMensaje] = useState<{ readonly tono: "exito" | "error"; readonly texto: string } | null>(null);

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.pendientes();
    if (r.ok) {
      setPendientes(r.datos);
      setEstado("ok");
      return;
    }
    if (r.estado === 404 || r.estado === 401) setEstado("no-encontrado");
    else if (r.estado === 403) setEstado("sin-mfa");
    else setEstado("error");
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function decidir(p: Pendiente, decision: "ACTIVE" | "REJECTED"): Promise<void> {
    setMensaje(null);
    setOcupado(p.organizationId);
    const r = await api.revisar(p.organizationId, decision, decision === "REJECTED" ? motivo : undefined);
    setOcupado(null);
    if (r.ok) {
      setRechazando(null);
      setMensaje({
        tono: "exito",
        texto: decision === "ACTIVE" ? t.apoderamientos.validada(p.legalName) : t.apoderamientos.rechazada(p.legalName),
      });
    } else {
      setMensaje({ tono: "error", texto: t.comun.error });
    }
    // Se recarga en los dos casos: si otra persona decidió antes, la lista lo dice.
    await cargar();
  }

  if (estado === "cargando") return <p className="suave">{t.comun.cargando}</p>;
  if (estado === "no-encontrado") return <p>{t.comun.noEncontrado}</p>;
  if (estado === "sin-mfa") return <p className="aviso" data-tono="aviso">{t.comun.sinMfa}</p>;
  if (estado === "error") return <p className="aviso" data-tono="error">{t.comun.error}</p>;

  return (
    <>
      <h1>{t.apoderamientos.titulo}</h1>
      <p className="aviso" data-tono="aviso">{t.apoderamientos.antes}</p>
      {mensaje !== null ? (
        <p className="aviso" data-tono={mensaje.tono} role="status">
          {mensaje.texto}
        </p>
      ) : null}

      {pendientes.length === 0 ? (
        <p className="suave">{t.apoderamientos.vacio}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t.apoderamientos.columnas.nombre}</th>
              <th>{t.apoderamientos.columnas.nif}</th>
              <th>{t.apoderamientos.columnas.ruta}</th>
              <th>{t.apoderamientos.columnas.declarado}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pendientes.map((p) => (
              <tr key={p.organizationId}>
                <td>
                  {p.legalName}
                  <div className="suave">{p.organizationId}</div>
                </td>
                <td>{p.taxId}</td>
                <td>{p.route ?? ""}</td>
                <td>{fechaLegible(p.declaredAt)}</td>
                <td>
                  <button type="button" onClick={() => void decidir(p, "ACTIVE")} disabled={ocupado !== null}>
                    {t.apoderamientos.validar}
                  </button>{" "}
                  <button
                    type="button"
                    data-variante="secundario"
                    onClick={() => setRechazando(rechazando === p.organizationId ? null : p.organizationId)}
                    disabled={ocupado !== null}
                  >
                    {rechazando === p.organizationId ? t.apoderamientos.cancelar : t.apoderamientos.rechazar}
                  </button>
                  {rechazando === p.organizationId ? (
                    <div>
                      <label htmlFor={`motivo-${p.organizationId}`}>
                        <span>{t.apoderamientos.motivo}</span>
                        <select
                          id={`motivo-${p.organizationId}`}
                          value={motivo}
                          onChange={(e) => setMotivo(e.target.value as MotivoRechazo)}
                        >
                          {MOTIVOS.map((m) => (
                            <option key={m.valor} value={m.valor}>
                              {m.texto}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button type="button" onClick={() => void decidir(p, "REJECTED")} disabled={ocupado !== null}>
                        {t.apoderamientos.rechazar}
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
